import React, { useMemo } from 'react';
import { Building2, CalendarCheck, Check, Clock, MapPin, User, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useOnboarding, type OnboardingPhaseId } from '../../contexts/OnboardingContext';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * True when the wizard was reopened as a tutorial. The first-run gate is not
   * dismissible — closing it used to strand people on a half-configured account
   * with no way back in.
   */
  dismissible?: boolean;
}

const PHASE_ICONS: Record<OnboardingPhaseId, LucideIcon> = {
  brand: Building2,
  you: User,
  area: MapPin,
  hours: Clock,
  calendar: CalendarCheck,
};

type PhaseState = 'complete' | 'current' | 'upcoming';

export const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  children,
  footer,
  dismissible = false,
}) => {
  const { currentStep, steps, phases, goToStep, isStepAccessible, closeModal } = useOnboarding();

  const active = steps[currentStep];

  // Steps outnumber the labels the header can show, so progress is reported at
  // two resolutions: named phases for orientation, and a bar for exact
  // position. Both read from the same index so they can't disagree.
  const progressPercent = Math.round(((currentStep + 1) / Math.max(1, steps.length)) * 100);

  const phaseModel = useMemo(
    () =>
      phases.map(phase => {
        const phaseSteps = steps.filter(s => s.phase === phase.id);
        const state: PhaseState = phaseSteps.some(s => s.id === currentStep)
          ? 'current'
          : phaseSteps.every(s => s.completed)
            ? 'complete'
            : 'upcoming';

        const entryStep = phaseSteps.find(s => !s.completed) ?? phaseSteps[0];
        return {
          ...phase,
          state,
          stepCount: phaseSteps.length,
          entryStepId: entryStep?.id ?? 0,
          accessible: entryStep ? isStepAccessible(entryStep.id) : false,
        };
      }),
    [phases, steps, currentStep, isStepAccessible]
  );

  const activePhase = phaseModel.find(p => p.state === 'current');
  const positionInPhase = activePhase
    ? steps.filter(s => s.phase === activePhase.id).findIndex(s => s.id === currentStep) + 1
    : 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div
        className="absolute inset-0 bg-ink-950/70 backdrop-blur-md"
        onClick={dismissible ? closeModal : undefined}
        aria-hidden="true"
      />

      {/* Full-bleed on phones so the step owns the whole viewport; a centered
          card from `sm` up. */}
      <div className="relative flex h-full items-center justify-center sm:p-4 lg:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Account setup"
          className="onboarding-modal flex h-full w-full max-w-3xl flex-col bg-surface shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl lg:max-h-[92vh]"
          onClick={e => e.stopPropagation()}
        >
          <header className="flex-shrink-0 border-b border-ink-100 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 sm:px-6 sm:pt-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-2xs font-semibold uppercase tracking-wide text-accent-600">
                  {activePhase?.title ?? 'Setup'}
                  {activePhase && activePhase.stepCount > 1 && (
                    <span className="text-ink-400">
                      {' '}
                      · {positionInPhase} of {activePhase.stepCount}
                    </span>
                  )}
                </p>
                <h1 className="truncate text-base font-bold leading-tight text-ink-900 sm:text-lg">
                  {active?.title}
                </h1>
              </div>

              <div className="flex flex-shrink-0 items-center gap-2">
                <span className="whitespace-nowrap text-xs font-medium tabular-nums text-ink-400">
                  {currentStep + 1}/{steps.length}
                </span>
                {dismissible && (
                  // eslint-disable-next-line no-restricted-syntax -- dialog close affordance; IconButton renders a visible chrome treatment we don't want here
                  <button
                    type="button"
                    onClick={closeModal}
                    aria-label="Close setup guide"
                    className="-mr-1 rounded-lg p-2 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            <nav aria-label="Setup progress">
              <ol className="flex items-center gap-1 sm:gap-1.5">
                {phaseModel.map(phase => {
                  const Icon = PHASE_ICONS[phase.id];
                  const isCurrent = phase.state === 'current';

                  return (
                    <li key={phase.id} className="flex min-w-0 flex-1 items-center">
                      {/* eslint-disable-next-line no-restricted-syntax -- progress entry combining a state chip, label and rail; no primitive covers it */}
                      <button
                        type="button"
                        onClick={() => phase.accessible && goToStep(phase.entryStepId)}
                        disabled={!phase.accessible}
                        aria-current={isCurrent ? 'step' : undefined}
                        title={phase.title}
                        className={`group flex min-w-0 flex-1 flex-col gap-1.5 rounded-lg py-1 text-left transition-opacity ${
                          phase.accessible
                            ? 'cursor-pointer hover:opacity-80'
                            : 'cursor-not-allowed'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-full rounded-full transition-colors ${
                            phase.state === 'complete'
                              ? 'bg-success-500'
                              : isCurrent
                                ? 'bg-accent-600'
                                : 'bg-ink-200'
                          }`}
                        />
                        <span className="flex items-center gap-1.5">
                          <span
                            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md transition-colors ${
                              phase.state === 'complete'
                                ? 'bg-success-100 text-success-700'
                                : isCurrent
                                  ? 'bg-accent-100 text-accent-700'
                                  : 'bg-ink-100 text-ink-400'
                            }`}
                          >
                            {phase.state === 'complete' ? (
                              <Check className="h-3 w-3" strokeWidth={3} />
                            ) : (
                              <Icon className="h-3 w-3" />
                            )}
                          </span>
                          {/* Labels are the first thing to go when space is
                              tight — the rail above still communicates order. */}
                          <span
                            className={`hidden truncate text-11 font-medium sm:inline ${
                              isCurrent ? 'text-ink-900' : 'text-ink-400'
                            }`}
                          >
                            {phase.title}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>

            <span className="sr-only" role="status">
              Step {currentStep + 1} of {steps.length}, {progressPercent}% complete
            </span>
          </header>

          {/* overflow-x-hidden keeps a single wide child from making the whole
              step horizontally scrollable (the "cut off" look on mobile). */}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
            <div className="mx-auto w-full max-w-2xl px-4 py-5 sm:px-6 sm:py-6">{children}</div>
          </div>

          {footer && (
            <div className="flex-shrink-0 border-t border-ink-100 bg-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pb-4 sm:pt-4">
              <div className="mx-auto w-full max-w-2xl">{footer}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
